import { rem, Select } from '@mantine/core';
import { GridApi } from 'ag-grid-community';
import { get } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { PriceOfferRow } from 'components/tables/data/price-offer/types';
import useFilterValue, { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';

interface PriceOfferUserSelectFilterProps {
  api: GridApi<PriceOfferRow>;
  filterKey: 'assignedTo.userId' | 'createdBy.userId';
  userPath: 'assignedTo' | 'createdBy';
  placeholder: string;
  onChange?: FilterChangeCallback;
}

interface ConfiguredPriceOfferUserSelectFilterProps {
  api: GridApi<PriceOfferRow>;
  onChange?: FilterChangeCallback;
}

interface ConfigurePriceOfferUserSelectFilterProps {
  filterKey: PriceOfferUserSelectFilterProps['filterKey'];
  userPath: PriceOfferUserSelectFilterProps['userPath'];
  placeholder: string;
}

type PriceOfferUser = PriceOfferRow['assignedTo'];

/**
 * User filter for assignee/creator columns in the price-offer table.
 */
function PriceOfferUserSelectFilter({
  api,
  filterKey,
  userPath,
  placeholder,
  onChange,
}: PriceOfferUserSelectFilterProps) {
  const [value, setValue] = useFilterValue({ api, filterKey, onChange });
  const [users, setUsers] = useState<PriceOfferUser[]>([]);

  const options = useMemo(
    () =>
      users
        .map(({ userId, fullName, firstName, lastName }) => ({
          value: String(userId),
          label: fullName ?? [firstName, lastName].filter(Boolean).join(' ') ?? String(userId),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users]
  );

  useEffect(() => {
    /**
     * Finds unique users currently available in the table model.
     */
    const findUniqueUsers = () => {
      const uniqueUsers = new Map<number, PriceOfferUser>();

      api.forEachNode(({ data }) => {
        const user = get(data, userPath) as PriceOfferUser | undefined;

        if (user && !uniqueUsers.has(user.userId)) {
          uniqueUsers.set(user.userId, user);
        }
      });

      setUsers(Array.from(uniqueUsers.values()));
    };

    findUniqueUsers();

    api.addEventListener('modelUpdated', findUniqueUsers);
    return () => api.removeEventListener('modelUpdated', findUniqueUsers);
  }, [api, userPath]);

  return (
    <Select
      data={options}
      clearable
      w="100%"
      size="sm"
      placeholder={placeholder}
      value={value}
      onChange={setValue}
      styles={{ dropdown: { minWidth: rem(220) } }}
    />
  );
}

PriceOfferUserSelectFilter.configure =
  (config: ConfigurePriceOfferUserSelectFilterProps) => (props: ConfiguredPriceOfferUserSelectFilterProps) =>
    <PriceOfferUserSelectFilter {...config} {...props} />;

export default PriceOfferUserSelectFilter;
